import { ValidatorInfo } from '@espoon-voltti/ts-libs/lib/validation'

type ValidationMessage = (x: unknown) => string
type Validations = Record<string, ValidationMessage>

export const fi: Validations = {
  attachmentError: () => `Virheellinen tiedosto`,
  attachmentLoading: () => `Tiedoston lataamisessa tapahtui virhe`,
  dateWithinRange: ({ start, end }) => `Pitää olla välillä ${start} - ${end}`,
  greaterThanOrEqual: ({ min, unit }) =>
    `Pitää olla vähintään ${min}${unit ? ' ' + unit : ''}`,
  nonEmptyArray: () => `Tyhjä lista e ole sallittu`,
  notNil: () => `Arvo puuttuu`,
  required: () => `Pakollinen tieto`,
  withinRange: ({ min, unit, max }) =>
    `Pitää olla välillä ${min} ${unit} - ${max} ${unit}`,
  invalidEmail: () => `Virheellinen sähköpostiosoite`,
  invalidPhone: () => `Virheellinen puhelinnumero`,
  maxLength: () => `Arvo on liian pitkä`,
  minLength: ({ minLength }) => `Pitää olla vähintään ${minLength} merkkiä`,
  invalidIban: () => `Virheellinen tilinumero`,
  arrayMinLength: ({ minLength }) => `Pitää olla vähintään ${minLength}`,
  attachmentsRequired: ({ min }) => `Liitteitä pitää olla vähintään ${min}`,
  dateAfterInfo: ({ start }) => `Päivämäärän pitää olla ${start} jälkeen`,
  lessThanOrEqual: ({ max }) => `Arvon on oltava ${max} tai pienempi`,
  dateSameOrBefore: ({ start }) => `Päivämäärän pitää olla ${start} tai ennen`,
  dateSameOrAfter: ({ start }) => `Päivämäärän pitää olla ${start} jälkeen`,
  duplicateSigners: () => `Kaksi samaa allekirjoittajaa`,
  invalidSsn: () => `Virheellinen henkilötunnus`,

  // admin specific
  ageDistTotal: ({ min }) => `Arvon on oltava ${min} tai suurempi`,
  currentYear: () => `Päivämäärän on oltava kuluvana vuonna`,
  date: ({ actual }) => `${actual} ei ole päivämäärä`,
  eventStartDateMissingEndDateGiven: () => `Arvo puuttuu`,
  futureOrPresent: ({ dateToCompare }) =>
    `Päivämäärän on oltava ${dateToCompare} tai sen jälkeen`,
  beforeOrPresent: ({ dateToCompare }) =>
    `Päivämäärän on oltava ${dateToCompare} tai sitä ennen`,
  invalidSignerAmount: () => `Ei vaadittua määrää allekirjoittajia`,
  noRepresentatives: () => `Ei edustajia`,
  notSigner: () => `Ei allekirjoitusoikeutta`,
  timeSameOrAfter: ({ start }) => `Ajan on oltava ${start} jälkeen`,
  digitsOnly: () => `Vain numerot 0-9`
}

export const en: typeof fi = {
  attachmentError: () => `Invalid file`,
  attachmentLoading: () => `An error occurred while uploading the file`,
  dateWithinRange: ({ start, end }) =>
    `Value must be between ${start} - ${end}`,
  greaterThanOrEqual: ({ min, unit }) =>
    `Value must be ${min}${unit} or greater`,
  nonEmptyArray: () => `Empty list not allowed`,
  notNil: () => `Value is missing`,
  required: () => `Mandatory field`,
  withinRange: ({ min, max, unit }) =>
    `Must be between ${min}${unit} - ${max}${unit}`,
  invalidEmail: () => `Invalid email address`,
  invalidPhone: () => `Invalid telephone number`,
  maxLength: () => `Value too long`,
  minLength: ({ minLength }) => `Must be at least ${minLength} characters`,
  invalidIban: () => `Invalid account number`,
  arrayMinLength: ({ minLength }) =>
    `Must contain at least ${minLength} values`,
  attachmentsRequired: ({ min }) => `At least ${min} attachments are required`,
  dateAfterInfo: ({ start }) => `Date must be after ${start}`,
  lessThanOrEqual: ({ max }) => `Must be ${max} or less`,
  dateSameOrBefore: ({ start }) => `Date must be ${start} or before`,
  dateSameOrAfter: ({ start }) => `Date must be ${start} or after`,
  duplicateSigners: () => `Two signers cannot be the same`,
  invalidSsn: () => `Invalid ssn`
}

export const sv: typeof fi = {
  attachmentError: () => `Felaktig fil`,
  attachmentLoading: () => `Det uppstod ett fel vid nedladdningen av filen`,
  dateWithinRange: ({ start, end }) => `Det ska vara mellan ${start} - ${end}`,
  greaterThanOrEqual: ({ min, unit }) =>
    `Värdet måste vara ${min}${unit} eller större`,
  nonEmptyArray: () => `En tom lista är inte tillåten`,
  notNil: () => `Värdet saknas`,
  required: () => `Obligatoriskt fält`,
  withinRange: ({ min, max, unit }) =>
    `Det ska vara mellan ${min}${unit} - ${max}${unit}`,
  invalidEmail: () => `Felaktig e-postadress`,
  invalidPhone: () => `Felaktigt telefonnummer`,
  maxLength: () => `Värde är för länge`,
  minLength: ({ minLength }) => `Måste vara minst ${minLength} tecken`,
  invalidIban: () => `Felaktigt kontonummer`,
  arrayMinLength: ({ minLength }) => `Måste ha ${minLength} aller mera värder`,
  attachmentsRequired: ({ min }) => `Måste ha mindre ${min} bilagorna`,
  dateAfterInfo: ({ start }) => `Datumet måste vara ${start} eller senare`,
  lessThanOrEqual: ({ max }) => `Värdet måste vara ${max} eller mindre`,
  dateSameOrBefore: ({ start }) => `	Datumet måste vara ${start} eller tidigare`,
  dateSameOrAfter: ({ start }) => `	Datumet måste vara ${start} eller senare`,
  duplicateSigners: () => `Två av undertecknarna kan inte vara samma person`,
  invalidSsn: () => `Felaktig personbeteckning`
}

export const validationMessage =
  (lang: 'fi' | 'en' | 'sv') =>
  <T extends ValidatorInfo>(x: T) => {
    switch (lang) {
      case 'en':
        return en[x.type](x)
      case 'sv':
        return sv[x.type](x)
      case 'fi':
        return fi[x.type](x)
      default:
        const _: never = lang
        return _
    }
  }
